import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Phone, Mail, MessageCircle } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useSiteContact } from "@/lib/siteContact";

const Footer = () => {
    const { settings } = useSettings() || {};
    const contact = useSiteContact();
    const s = settings || {};
    const wa = (s.social_whatsapp || contact.whatsappDigits || "").replace(/\D/g, "");

    return (
        <footer className="bg-[#0f0f12] text-white mt-12 sm:mt-20" data-testid="site-footer">
            <div className="container-x py-10 sm:py-16 grid grid-cols-1 md:grid-cols-4 gap-8 sm:gap-10">
                <div className="md:col-span-1">
                    <div className="flex items-center gap-3 mb-4">
                        <img
                            src="/assets/servall-logo.webp"
                            alt="Servall Bikes"
                            className="h-12 sm:h-14 w-auto shrink-0 object-contain bg-white rounded-md"
                        />
                        <div className="leading-tight">
                            <p className="font-display text-xl tracking-tight">SERVALL</p>
                            <p className="text-[8px] text-white/60 font-semibold uppercase tracking-wide mt-0.5">Authorized Bike Sales</p>
                        </div>
                    </div>
                    <p className="text-sm text-[#a0a0a8] leading-relaxed">{s.site_tagline || "Multi-brand two-wheeler sales — compare, quote, and buy from authorized Servall outlets."}</p>
                </div>

                <div>
                    <h4 className="font-heading font-semibold text-sm mb-4">Explore</h4>
                    <ul className="space-y-2 text-sm text-[#a0a0a8]">
                        <li><Link to="/bikes" className="inline-flex items-center min-h-[44px] hover:text-white" data-testid="footer-bikes">All Bikes</Link></li>
                        <li><Link to="/emi" className="inline-flex items-center min-h-[44px] hover:text-white" data-testid="footer-emi">EMI Calculator</Link></li>
                        <li><Link to="/compare" className="inline-flex items-center min-h-[44px] hover:text-white" data-testid="footer-compare">Compare Bikes</Link></li>
                        <li><Link to="/dealers" className="inline-flex items-center min-h-[44px] hover:text-white" data-testid="footer-dealers">Branch Locator</Link></li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-heading font-semibold text-sm mb-4">Resources</h4>
                    <ul className="space-y-2 text-sm text-[#a0a0a8]">
                        <li><Link to="/news" className="inline-flex items-center min-h-[44px] hover:text-white">News & tips</Link></li>
                        <li><Link to="/about" className="inline-flex items-center min-h-[44px] hover:text-white" data-testid="footer-about">About us</Link></li>
                        <li><Link to="/privacy-policy" className="inline-flex items-center min-h-[44px] hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-heading font-semibold text-sm mb-4">Contact</h4>
                    <ul className="space-y-2 text-sm text-[#a0a0a8]">
                        <li><Link to="/contact" className="inline-flex items-center min-h-[44px] hover:text-white">Send a message</Link></li>
                        <li><a href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 min-h-[44px] hover:text-white break-all" data-testid="footer-whatsapp"><Phone size={14} /> {contact.phoneDisplay}</a></li>
                        <li><a href={`mailto:${contact.email}`} className="flex items-center gap-2 min-h-[44px] hover:text-white break-all" data-testid="footer-email"><Mail size={14} /> {contact.email}</a></li>
                    </ul>
                    <div className="flex items-center gap-3 mt-4">
                        {s.social_instagram && (
                            <a href={s.social_instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
                                className="min-w-[44px] min-h-[44px] rounded-full border border-[#2a2a2e] flex items-center justify-center hover:bg-[#ED1C24] hover:border-[#ED1C24]" data-testid="footer-instagram">
                                <Instagram size={16} />
                            </a>
                        )}
                        {s.social_youtube && (
                            <a href={s.social_youtube} target="_blank" rel="noreferrer" aria-label="YouTube"
                                className="min-w-[44px] min-h-[44px] rounded-full border border-[#2a2a2e] flex items-center justify-center hover:bg-[#ED1C24] hover:border-[#ED1C24]" data-testid="footer-youtube">
                                <Youtube size={16} />
                            </a>
                        )}
                        {wa && (
                            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" aria-label="WhatsApp"
                                className="min-w-[44px] min-h-[44px] rounded-full border border-[#2a2a2e] flex items-center justify-center hover:bg-[#10B981] hover:border-[#10B981]" data-testid="footer-whatsapp">
                                <MessageCircle size={16} />
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-t border-[#2a2a2e]">
                <div className="container-x py-5 text-xs text-[#7a7a84] text-center">
                    {s.footer_text || "© 2026 Servall Bikes. All rights reserved."}
                </div>
            </div>
        </footer>
    );
};

export default Footer;
